import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DWS } from '@interfaces/dws';
import { resource } from '@util/resource';
import * as moment from 'moment';
import { resolve } from '@util/resource/resolve';

export enum ReceiptFiscalOperation {
  RECEIPT = 'RECEIPT',
  CORRECTION = 'CORRECTION',
  CANCELLATION = 'CANCELLATION',
}

export enum ReceiptSaleDocumentState {
  NONE = 'NONE',
  FULL_RETURN = 'FULL_RETURN',
  PARTIAL_RETURN = 'PARTIAL_RETURN',
  VOIDED = 'VOIDED',
}

export interface Receipt {
  id: string;
  wineryId: string;
  integrationId: string;
  pointOfSaleWineSuiteId?: string;
  wineryChannelId?: string;
  refDate?: string;
  progressiveNumber?: number;
  externalReceiptId?: string;
  /** Order / reservation / sale entity id from CRM */
  externalEntityId?: string;
  internalDocumentNumber?: string;
  adeDocumentNumber?: string;
  referencedReceiptId?: string;
  /** WS-… of the original receipt; set by API for reso/storno (not only when it is on the same list page). */
  referencedInternalDocumentNumber?: string;
  /** WS-2522: operator reason for CANCELLATION; shown in receipt detail only, not sent to Fiskaly. */
  cancellationReason?: string;
  receiptType: ReceiptType;
  receiptData: string;
  publicLink?: string;
  pdfLink?: string;
  qrCodeData?: string;
  status: ReceiptStatus;
  authorityCommunicationState?: ReceiptAuthorityCommunicationState;
  /** WS-2496/2524 */
  fiscalOperation?: ReceiptFiscalOperation;
  saleDocumentState?: ReceiptSaleDocumentState;
  extraData?: string;
  createdAt: string;
  updatedAt: string;
  totalCents?: number;
  totalNoVatCents?: number;
  vatAmountCents?: number;
  discountTotalCents?: number;
}

export enum ReceiptType {
  ORDER = 'ORDER',
  RESERVATION = 'RESERVATION',
  PREORDER = 'PREORDER',
  GIFT = 'GIFT'
}

export enum ReceiptStatus {
  CREATED = 'CREATED',        // receipt created in DB (validated)
  PENDING = 'PENDING',        // eReceipt created, waiting for Sign-IT
  PROCESSING = 'PROCESSING',
  SIGNED = 'SIGNED',          // Sign-IT transaction successful
  ARCHIVED = 'ARCHIVED',      // archived to SAFE
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  RECEIPT = 'RECEIPT',        // new receipt flow
  CANCELLATION = 'CANCELLATION', // storno – cancellation flow
  CORRECTION = 'CORRECTION'   // cambio – correction flow
}

export enum ReceiptAuthorityCommunicationState {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  STORNED = 'STORNED',
  PENDING = 'PENDING'
}

export interface ReceiptFilter {
  page?: number;
  count?: number;
  sortBy?: string;
  wineryId?: string;
  integrationId?: string;
  externalReceiptId?: string;
  externalEntityId?: string;
  receiptType?: ReceiptType;
  status?: ReceiptStatus | string;
  authorityCommunicationState?: ReceiptAuthorityCommunicationState | string;
  startDate?: string;
  endDate?: string;
  customerName?: string;
  pointOfSaleWineSuiteId?: string;
  pointOfSaleWineSuiteIds?: string;
  wineryChannelId?: string;
  wineryChannelIds?: string;
  progressiveNumber?: number;
  minTotalCents?: number;
  maxTotalCents?: number;
  terminalId?: string;
  terminalName?: string;
  fiscalOperation?: ReceiptFiscalOperation | string;
  fiscalOperations?: string;
  saleDocumentState?: ReceiptSaleDocumentState | string;
  saleDocumentStates?: string;
  /** Comma-separated UI document statuses (GENERATED, CANCELLED, FULL_RETURN, …) */
  documentStatus?: string;
  authorityCommunicationStates?: string;
  totalRange?: { gte?: number; lte?: number };
  dateRange?: { start?: string; end?: string };
}

export interface ReceiptPage extends DWS.Page<Receipt> {}

export interface FiscalAuditLog {
  id: string;
  receiptId?: string;
  pointOfSaleId?: string;
  wineryId: string;
  documentNumber?: string;
  documentType?: string;        // RECEIPT | REVERSAL | CLOSURE
  transactionTimestamp?: string;
  sentAt?: string;
  adeConfirmedAt?: string;
  fiskalyTransactionId?: string;
  sdiTransmissionId?: string;
  status: string;               // PENDING | ACCEPTED | FAILED
  totalGrossCents?: number;
  taxableCustomCents?: number;
  vatCustomCents?: number;
  taxable22Cents?: number;
  vat22Cents?: number;
  taxable10Cents?: number;
  vat10Cents?: number;
  taxable4Cents?: number;
  vat4Cents?: number;
  taxable0Cents?: number;
  vatExemptCents?: number;
  paymentMethod?: string;
  signatureHash?: string;
  requestPayload?: string;
  responsePayload?: string;
  adeResultCode?: string;
  adeResultMessage?: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReceiptsService {
  constructor(private http: HttpClient) {}

  /**
   * Checks if the winery is integrated with fiscality (receipt generation).
   * @returns Observable<boolean> indicating fiscality integration status
   */
  isFiscalityIntegrated(): Observable<boolean> {
    return resource('crm://receipts/is-fiscality-integrated')
      .get<boolean>();
  }

  /**
   * Force creates a receipt for the given object.
   * @param objId The ID of the object to create receipt for
   * @param objType The type of object (ORDER, RESERVATION, GIFT, PREORDER)
   * @returns Observable<any> with the receipt creation result
   */
  forceCreateReceipt(
    objId: string,
    objType: ReceiptType,
    bypassAutoReceiptFlags = false
  ): Observable<any> {
    return resource('crm://receipts/sync')
      .post<any>({ objId, objType, bypassAutoReceiptFlags });
  }

  /** WS-2497: new fiscal storno / CANCELLATION row + Fiskaly flow */
  createCancellationReceipt(
    originalReceiptId: string,
    body?: { reason?: string; extraData?: string }
  ): Observable<{ success: boolean; message?: string; newReceiptId?: string; originalReceiptId?: string }> {
    return resource('crm://receipts/id/cancellation')
      .params({ id: originalReceiptId })
      .post(body ?? {});
  }

  /** WS-2497: new fiscal reso (CORRECTION) row + Fiskaly flow */
  createCorrectionReceipt(
    originalReceiptId: string,
    body?: { reason?: string; extraData?: string }
  ): Observable<{ success: boolean; message?: string; newReceiptId?: string; originalReceiptId?: string }> {
    return resource('crm://receipts/id/correction')
      .params({ id: originalReceiptId })
      .post(body ?? {});
  }

  /** Same Sign-IT retry the ADE job runs when the receipt already has an eReceipt. */
  retrySignIt(receipt: Receipt): Observable<{
    success?: boolean;
    message?: string;
    error?: string;
    errorCode?: string;
    warning?: boolean;
  }> {
    return resource('crm://fiskaly/wineryId/receipts/receiptId/retry-signit')
      .params({
        wineryId: receipt.wineryId,
        receiptId: receipt.id,
        integrationId: receipt.integrationId,
      })
      .post({});
  }

  /**
   * Get receipt by external receipt ID using resource pattern.
   * @param externalReceiptId The external receipt ID
   * @returns Observable<Receipt>
   */
  getByExternalReceiptId(externalReceiptId: string): Observable<Receipt> {
    return resource(`crm://receipts/external/${externalReceiptId}`)
      .get<Receipt>();
  }

  /**
   * Get receipt by ID using resource pattern.
   * @param id The receipt ID
   * @returns Observable<Receipt>
   */
  get(id: string): Observable<Receipt> {
    return resource("crm://receipts/id").params({ id }).get<Receipt>();
  }

  /**
   * eReceipt PDF via CRM (DWS). Server applies integration credentials; do not use raw pdfLink in the browser.
   */
  getReceiptPdfBlob(receiptId: string): Observable<Blob> {
    return resource('crm://receipts/id/pdf').params({ id: receiptId }).get('blob');
  }

  /**
   * List receipts with filtering using v2 query API.
   * @param filters Receipt filter parameters
   * @returns Observable<ReceiptPage>
   */
  listv2(filters: ReceiptFilter = {}): Observable<ReceiptPage> {
    return resource('crm://v2/receipts/query').post<ReceiptPage>(filters);
  }

  /**
   * Get receipts for a specific entity (order, reservation, etc.)
   * @param externalEntityId The entity ID
   * @param receiptType The type of receipt
   * @returns Observable<Receipt[]>
   */
  /**
   * @param externalEntityId For ORDER: CRM order {@code code} (often UUID), numeric order id, or sale UUID —
   *   backend resolves to sale id(s) via {@code sale_rows}.
   */
  getByEntity(externalEntityId: string, receiptType: ReceiptType): Observable<Receipt[]> {
    return resource('crm://receipts/list')
      .params({ externalEntityId, receiptType })
      .get<ReceiptPage>()
      .pipe(
        map(page => page.content || [])
      );
  }

  /**
   * Resend fiscal receipt by email: server builds content from transactional templates.
   * Optional language (defaults to Italian if omitted) and optional template type hint.
   */
  sendEmailResendGuestCopy(body: {
    receiptId: string;
    recipientEmails: string[];
    language?: string;
    /** @deprecated Prefer transactionalEmailId */
    transactionalTemplateType?: string;
    transactionalEmailId?: number;
  }): Observable<void> {
    return resource('crm://receipts/send-email').post<void>(body);
  }

  /**
   * @deprecated Legacy manual HTML send; prefer {@link sendEmailResendGuestCopy}.
   */
  sendEmail(emailData: {
    receiptId: string;
    subject: string;
    senderEmail: string;
    senderName: string;
    recipientEmail: string;
    body: string;
  }): Observable<any> {
    return resource('crm://receipts/send-email')
      .post<any>(emailData);
  }

  // -------------------------------------------------------------------------
  // Fiscal Audit Log
  // -------------------------------------------------------------------------

  getAuditLogs(receiptId: string): Observable<FiscalAuditLog[]> {
    return resource(`crm://receipts/${receiptId}/audit-logs`)
      .get<FiscalAuditLog[]>();
  }

  downloadAuditLogUrl(receiptId: string): string {
    // Returns the URL string; caller opens it or uses fetch for file download
    return `crm://receipts/${receiptId}/audit-logs/export`;
  }

  /**
   * Fetches one page of audit logs for a winery / period (JSON, for client-side XLSX assembly).
   */
  getAuditLogPage(
    wineryId: string,
    startDate: string,
    endDate: string,
    page: number = 0,
    size: number = 10000
  ): Observable<{ data: FiscalAuditLog[]; totalPages: number }> {
    const url = resolve('crm://receipts/audit-logs/export');
    const params = new HttpParams()
      .set('wineryId', wineryId)
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<FiscalAuditLog[]>(url, { observe: 'response', params }).pipe(
      map((res: HttpResponse<FiscalAuditLog[]>) => ({
        data: res.body ?? [],
        totalPages: parseInt(res.headers.get('X-Total-Pages') ?? '1', 10),
      }))
    );
  }
}
